import apiClient from '@/shared/api/client'

export interface DateRangeParams {
  plant_id: string
  date_from: string  // YYYY-MM-DD
  date_to: string
  [key: string]: string | undefined
}

// ── Gate / Weighbridge ────────────────────────────────────────────────────────

export const reportsApi = {
  // Gate
  gateSummary: async (params: DateRangeParams & { entry_type?: string }) =>
    (await apiClient.get('/reports/gate-summary', { params })).data,

  gateEntriesExportUrl: (params: DateRangeParams & { entry_type?: string; status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/gate-entries/export?${q}`
  },

  weighbridgeReconciliation: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/weighbridge-reconciliation', { params })).data,

  weighbridgeExportUrl: (params: DateRangeParams) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/weighbridge-reconciliation/export?${q}`
  },

  shortageAnalysis: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/shortage-analysis', { params })).data,

  visitorSummary: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/visitor-summary', { params })).data,

  visitorExportUrl: (params: DateRangeParams & { status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/visitor/export?${q}`
  },

  // Quality
  qualityTests: async (params: DateRangeParams & { sample_type?: string; result?: string; count_id?: string }) =>
    (await apiClient.get('/reports/quality-tests', { params })).data,

  qualityTestsExportUrl: (params: DateRangeParams & { sample_type?: string; result?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/quality-tests/export?${q}`
  },

  qualitySummaryByCount: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/quality-summary-by-count', { params })).data,

  capaAging: async (params: { plant_id: string; status?: string }) =>
    (await apiClient.get('/reports/capa-aging', { params })).data,

  capaAgingExportUrl: (params: { plant_id: string; status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/capa-aging/export?${q}`
  },

  capaSummary: async (plant_id: string) =>
    (await apiClient.get('/reports/capa-summary', { params: { plant_id } })).data,

  complaints: async (params: { plant_id: string; date_from?: string; date_to?: string; status?: string }) =>
    (await apiClient.get('/reports/complaints', { params })).data,

  complaintsExportUrl: (params: { plant_id: string; date_from?: string; date_to?: string; status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/complaints/export?${q}`
  },

  // Production
  productionSummary: async (params: DateRangeParams & { department_code?: string }) =>
    (await apiClient.get('/reports/production-summary', { params })).data,

  productionSummaryExportUrl: (params: DateRangeParams & { department_code?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/production-summary/export?${q}`
  },

  productionKpi: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/production-kpi', { params })).data,

  wasteTracking: async (params: DateRangeParams & { department_code?: string; waste_type?: string }) =>
    (await apiClient.get('/reports/waste-tracking', { params })).data,

  wasteTrackingExportUrl: (params: DateRangeParams & { department_code?: string; waste_type?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/waste-tracking/export?${q}`
  },

  wasteSummary: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/waste-summary', { params })).data,

  machineBreakdown: async (params: DateRangeParams & { category?: string }) =>
    (await apiClient.get('/reports/machine-breakdown', { params })).data,

  machineBreakdownExportUrl: (params: DateRangeParams & { category?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/machine-breakdown/export?${q}`
  },

  mttrMtbf: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/mttr-mtbf', { params })).data,

  mttrMtbfExportUrl: (params: DateRangeParams) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/mttr-mtbf/export?${q}`
  },

  // KPI Dashboard
  kpiDashboard: async (params: DateRangeParams) =>
    (await apiClient.get('/reports/kpi-dashboard', { params })).data,

  // Full Plant Report
  fullPlantReportExportUrl: (params: DateRangeParams) => {
    const q = new URLSearchParams(params as Record<string, string>)
    return `/api/v1/reports/full-plant-report/export?${q}`
  },
}

// ── Quality Tests ─────────────────────────────────────────────────────────────

export const qualityTestsApi = {
  meta: async () => (await apiClient.get('/quality-tests/meta')).data,
  list: async (params: { plant_id: string; sample_type?: string; result?: string; limit?: number }) =>
    (await apiClient.get('/quality-tests', { params })).data,
  get: async (id: string) => (await apiClient.get(`/quality-tests/${id}`)).data,
  create: async (data: Record<string, unknown>) =>
    (await apiClient.post('/quality-tests', data)).data,
  update: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch(`/quality-tests/${id}`, data)).data,
}

// ── Production Logs ───────────────────────────────────────────────────────────

export const productionLogsApi = {
  meta: async () => (await apiClient.get('/production-meta')).data,
  listLogs: async (params: { plant_id: string; department_code?: string; limit?: number }) =>
    (await apiClient.get('/production-logs', { params })).data,
  createLog: async (data: Record<string, unknown>) =>
    (await apiClient.post('/production-logs', data)).data,
  updateLog: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch(`/production-logs/${id}`, data)).data,
  listBreakdowns: async (params: { plant_id: string; machine_id?: string; category?: string; limit?: number }) =>
    (await apiClient.get('/breakdowns', { params })).data,
  createBreakdown: async (data: Record<string, unknown>) =>
    (await apiClient.post('/breakdowns', data)).data,
  resolveBreakdown: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch(`/breakdowns/${id}/resolve`, data)).data,
  listWaste: async (params: { plant_id: string; department_code?: string; waste_type?: string; limit?: number }) =>
    (await apiClient.get('/waste-records', { params })).data,
  createWaste: async (data: Record<string, unknown>) =>
    (await apiClient.post('/waste-records', data)).data,
}

// ── CAPA & Complaints ─────────────────────────────────────────────────────────

export const capaApi = {
  meta: async () => (await apiClient.get('/capa/meta')).data,
  listCapas: async (params: { plant_id: string; status?: string; severity?: string; limit?: number }) =>
    (await apiClient.get('/capa', { params })).data,
  createCapa: async (data: Record<string, unknown>) =>
    (await apiClient.post('/capa', data)).data,
  updateCapa: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch(`/capa/${id}`, data)).data,
  listComplaints: async (params: { plant_id: string; status?: string; limit?: number }) =>
    (await apiClient.get('/complaints', { params })).data,
  createComplaint: async (data: Record<string, unknown>) =>
    (await apiClient.post('/complaints', data)).data,
  updateComplaint: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch(`/complaints/${id}`, data)).data,
}
